import { PurchaseDocListItem } from "types/InvoiceInbox";
import { isMarkedPaid } from "utils/invoiceInboxStatus";
import {
  approvePurchaseDoc,
  deletePurchaseDoc,
  dispatchPurchaseDoc,
  restorePurchaseDoc,
  setPurchaseDocBookkept,
  setPurchaseDocMarkedPaid,
  setPurchaseDocPaymentSource,
} from "services/invoice-inbox.service";

/**
 * Bulk actions over the register's selected rows.
 *
 * Deliberately a loop over the SAME per-document endpoints the panel uses, not
 * a bulk endpoint: every rule — dispatch blockers, separation of duties, whose
 * slot an approval is, the exported-can't-be-removed guard — stays in exactly
 * one place on the server, and each document keeps its own event and audit
 * row. At 50–200 invoices a month a page of requests is nothing.
 *
 * `isEligible` only decides which buttons are offered and how many documents
 * each would touch. It is not the gate: a document the server still refuses
 * comes back as a failure with its reason, and stays selected.
 */

export type BulkActionId =
  | "dispatch"
  | "approve"
  | "bookkept"
  | "not_bookkept"
  | "paid"
  | "paid_company"
  | "paid_employee"
  | "delete"
  | "restore";

/** What the confirmation step collected — the same for every document in the batch. */
export type BulkInput = { reason: string; employeeId?: string };

export type BulkAction = {
  id: BulkActionId;
  labelKey: string;
  defaultLabel: string;
  tone?: "primary" | "critical";
  /** Asks for a confirmation (and, for removal, a reason) before running. */
  confirm?: boolean;
  needsReason?: boolean;
  /** Asks which employee before running (implies `confirm`). */
  needsEmployee?: boolean;
  isEligible: (doc: PurchaseDocListItem) => boolean;
  run: (companyId: string, doc: PurchaseDocListItem, input: BulkInput) => Promise<unknown>;
};

/**
 * The flags that make the panel show a RED banner. An approval is somebody
 * agreeing that the money may go out, and these are the two warnings that are
 * about exactly that — so a flagged document is approved from its own panel,
 * where the banner can be read, never as one row in a batch.
 */
export const hasCriticalFlag = (doc: PurchaseDocListItem) =>
  Boolean(doc.bankChangeFlagged || doc.suspiciousContent);

const live = (doc: PurchaseDocListItem) => !doc.deletedAt;

export const BULK_ACTIONS: BulkAction[] = [
  {
    id: "dispatch",
    labelKey: "invoice_inbox_send_for_approval",
    defaultLabel: "Send for approval",
    tone: "primary",
    confirm: true,
    // Mirrors only the STATUS half of the server's canDispatch. The rest
    // (supplier linked, amount present, bank confirmed, read) comes back as a
    // blocker list per document — which is the to-do list the person needs.
    // Sending also records the SENDER'S OWN accountant approval on the server
    // (InvoiceApprovalService.dispatch's accountantDecidedOnDispatch), so —
    // exactly like bulk Approve — it must never touch a document whose red
    // banner (bank change / suspicious content) nobody has read from its own
    // panel.
    isEligible: (doc) =>
      live(doc) && (doc.status === "needs_review" || doc.status === "rejected") && !hasCriticalFlag(doc),
    run: (companyId, doc) => dispatchPurchaseDoc(companyId, doc.id),
  },
  {
    id: "approve",
    labelKey: "approve",
    defaultLabel: "Approve",
    tone: "primary",
    confirm: true,
    isEligible: (doc) =>
      live(doc) && doc.status === "awaiting_approval" && doc.waitingForMe && !hasCriticalFlag(doc),
    run: (companyId, doc) => approvePurchaseDoc(companyId, doc.id),
  },
  {
    id: "bookkept",
    labelKey: "invoice_inbox_bulk_mark_bookkept",
    defaultLabel: "Mark entered",
    isEligible: (doc) => live(doc) && !doc.bookkeptAt,
    run: (companyId, doc) => setPurchaseDocBookkept(companyId, doc.id, true),
  },
  {
    id: "not_bookkept",
    labelKey: "invoice_inbox_bulk_mark_not_bookkept",
    defaultLabel: "Mark not entered",
    isEligible: (doc) => live(doc) && !!doc.bookkeptAt,
    run: (companyId, doc) => setPurchaseDocBookkept(companyId, doc.id, false),
  },
  {
    id: "paid",
    labelKey: "invoice_inbox_bulk_mark_paid",
    defaultLabel: "Mark as paid",
    isEligible: (doc) => live(doc) && !isMarkedPaid(doc),
    run: (companyId, doc) => setPurchaseDocMarkedPaid(companyId, doc.id, true),
  },
  {
    id: "paid_company",
    labelKey: "invoice_inbox_payment_source_company",
    defaultLabel: "Paid from the company account",
    isEligible: (doc) => live(doc) && doc.paymentSource !== "company",
    run: (companyId, doc) => setPurchaseDocPaymentSource(companyId, doc.id, { source: "company" }),
  },
  {
    id: "paid_employee",
    labelKey: "invoice_inbox_payment_source_employee",
    defaultLabel: "Paid by an employee",
    confirm: true,
    needsEmployee: true,
    // Every live document: one already marked for somebody else is exactly
    // what a batch re-pointing them would be for. Which employee is only known
    // after the pick, so a row already carrying that same person is skipped in
    // `run` rather than here — no request, no empty "changed" event.
    isEligible: (doc) => live(doc),
    run: (companyId, doc, input) =>
      doc.paymentSource === "employee" && doc.paidByEmployee === input.employeeId
        ? Promise.resolve()
        : setPurchaseDocPaymentSource(companyId, doc.id, { source: "employee", employeeId: input.employeeId }),
  },
  {
    id: "delete",
    labelKey: "delete",
    defaultLabel: "Remove",
    tone: "critical",
    confirm: true,
    needsReason: true,
    isEligible: (doc) => live(doc) && doc.status !== "exported",
    run: (companyId, doc, input) => deletePurchaseDoc(companyId, doc.id, input.reason),
  },
  {
    id: "restore",
    labelKey: "restore",
    defaultLabel: "Restore",
    isEligible: (doc) => !!doc.deletedAt,
    run: (companyId, doc) => restorePurchaseDoc(companyId, doc.id),
  },
];

export type BulkFailure = {
  doc: PurchaseDocListItem;
  code: string | null;
  /** Dispatch refusals: the server's list of what is missing. */
  blockers: string[];
  /** A sub-reason next to the code (e.g. which approver is not eligible). */
  reason: string | null;
  message: string | null;
};

export type BulkResult = {
  action: BulkActionId;
  succeeded: string[];
  failed: BulkFailure[];
};

/** How many requests are in the air at once — enough to be quick, few enough not to hammer the API. */
export const BULK_CONCURRENCY = 4;

export async function runBulkAction(
  action: BulkAction,
  companyId: string,
  docs: PurchaseDocListItem[],
  input: BulkInput,
  concurrency: number = BULK_CONCURRENCY
): Promise<BulkResult> {
  const succeeded: string[] = [];
  const failed: BulkFailure[] = [];
  let next = 0;

  const worker = async () => {
    while (next < docs.length) {
      const doc = docs[next];
      next += 1;
      try {
        await action.run(companyId, doc, input);
        succeeded.push(doc.id);
      } catch (err: any) {
        const data = (err && err.response && err.response.data) || {};
        failed.push({
          doc,
          code: data.code || null,
          blockers: Array.isArray(data.blockers) ? data.blockers : [],
          reason: data.reason || null,
          message: data.message || (err && err.message) || null,
        });
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, docs.length) }, worker));

  // Report failures in the order the rows are on screen, not in whatever order
  // the requests happened to finish.
  const order = new Map(docs.map((doc, index) => [doc.id, index]));
  failed.sort((a, b) => (order.get(a.doc.id) ?? 0) - (order.get(b.doc.id) ?? 0));
  return { action: action.id, succeeded, failed };
}
